import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

// ===========================================================================
// Dar de alta, corregir y retirar del maestro.
//
// El maestro llegaba solo del Excel del ERP y desde la web era de lectura, así
// que lo cotidiano no tenía salida: una razón social nueva, un número mal
// tecleado, una cuenta que se cerró.
//
// ⚠️ REIMPORTAR EL EXCEL PISA PARTE DE ESTO. `cargar-maestros` sobrescribe
// banco, moneda, CCI y empresa de las cuentas que ya existen, y vuelve a
// insertar las borradas. Se avisa en pantalla, porque descubrirlo después de
// corregir treinta cuentas es peor.
//
// RETIRAR NO ES BORRAR. Con movimientos, extractos o cruces detrás se DA DE
// BAJA: sale de las listas y el historial se queda. Solo lo que no tiene nada
// encima se borra de verdad. El servidor decide cuál de las dos cosas hace y
// lo dice en la respuesta; aquí no se adivina.
// ===========================================================================

type Empresa = {
  id: string;
  ruc: string;
  razonSocial: string;
  activa: boolean;
};

type Cuenta = {
  id: string;
  banco: string;
  numero: string;
  moneda: "PEN" | "USD";
  cci: string | null;
  alias: string | null;
  activa: boolean;
};

function Modal({
  titulo,
  children,
  acciones,
  alCerrar,
}: {
  titulo: string;
  children: React.ReactNode;
  /** Guardar va abajo, junto a Cancelar: es donde se busca al terminar. */
  acciones: React.ReactNode;
  alCerrar: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border bg-background p-5 shadow-lg">
        <h2 className="font-semibold text-[15px]">{titulo}</h2>
        {children}
        <p className="mt-3 text-[11px] text-muted-foreground">
          Reimportar el maestro del ERP puede volver a pisar banco, moneda, CCI
          y empresa de una cuenta que ya existía.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={alCerrar}>
            Cancelar
          </Button>
          {acciones}
        </div>
      </div>
    </div>
  );
}

function Campo({
  etiqueta,
  valor,
  alCambiar,
  ancho,
}: {
  etiqueta: string;
  valor: string;
  alCambiar: (v: string) => void;
  ancho?: string;
}) {
  return (
    <label className="block">
      <span className="font-medium text-[12px]">{etiqueta}</span>
      <input
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        className={`mt-0.5 w-full rounded border bg-background px-2 py-1.5 text-[12.5px] ${ancho ?? ""}`}
      />
    </label>
  );
}

/** Alta o corrección de una razón social. */
export function EditarEmpresa({
  empresa,
  alCerrar,
}: {
  /** `null` para dar de alta una nueva. */
  empresa: Empresa | null;
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const crear = api.crearEmpresa.useMutation();
  const editar = api.editarEmpresa.useMutation();
  const [ruc, setRuc] = useState(empresa?.ruc ?? "");
  const [razonSocial, setRazon] = useState(empresa?.razonSocial ?? "");

  const listo = /^\d{11}$/.test(ruc) && razonSocial.trim().length >= 2;
  const guardando = crear.isPending || editar.isPending;

  const alGuardar = () => {
    const fin = {
      onSuccess: (r: {
        status: number;
        body: { dice?: string; message?: string };
      }) => {
        if (r.status !== 200) {
          toast.error(r.body.message ?? "No se pudo guardar");
          return;
        }
        toast.success(r.body.dice ?? "Guardado");
        void qc.invalidateQueries();
        alCerrar();
      },
      onError: () => toast.error("No se pudo guardar"),
    };
    if (empresa)
      editar.mutate(
        { params: { id: empresa.id }, body: { ruc, razonSocial } },
        fin,
      );
    else crear.mutate({ body: { ruc, razonSocial } }, fin);
  };

  return (
    <Modal
      titulo={empresa ? "Corregir la razón social" : "Nueva razón social"}
      alCerrar={alCerrar}
      acciones={
        <Button size="sm" disabled={!listo || guardando} onClick={alGuardar}>
          {guardando ? "Guardando…" : empresa ? "Guardar" : "Dar de alta"}
        </Button>
      }
    >
      <div className="mt-3 space-y-3">
        <Campo etiqueta="RUC (11 dígitos)" valor={ruc} alCambiar={setRuc} />
        <Campo
          etiqueta="Razón social"
          valor={razonSocial}
          alCambiar={setRazon}
        />
      </div>
    </Modal>
  );
}

/** Alta o corrección de una cuenta bancaria. */
export function EditarCuenta({
  cuenta,
  empresaId,
  alCerrar,
}: {
  /** `null` para dar de alta una nueva. */
  cuenta: Cuenta | null;
  empresaId: string;
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const crear = api.crearCuenta.useMutation();
  const editar = api.editarCuenta.useMutation();
  const [banco, setBanco] = useState(cuenta?.banco ?? "");
  const [numero, setNumero] = useState(cuenta?.numero ?? "");
  const [moneda, setMoneda] = useState<"PEN" | "USD">(cuenta?.moneda ?? "PEN");
  const [cci, setCci] = useState(cuenta?.cci ?? "");
  const [alias, setAlias] = useState(cuenta?.alias ?? "");

  const listo = banco.trim().length >= 2 && numero.trim().length >= 4;
  const guardando = crear.isPending || editar.isPending;

  const alGuardar = () => {
    const fin = {
      onSuccess: (r: {
        status: number;
        body: { dice?: string; message?: string };
      }) => {
        if (r.status !== 200) {
          toast.error(r.body.message ?? "No se pudo guardar");
          return;
        }
        toast.success(r.body.dice ?? "Guardado");
        void qc.invalidateQueries();
        alCerrar();
      },
      onError: () => toast.error("No se pudo guardar"),
    };
    const datos = {
      banco,
      numero,
      moneda,
      cci: cci.trim() || null,
      alias: alias.trim() || null,
    };
    if (cuenta) editar.mutate({ params: { id: cuenta.id }, body: datos }, fin);
    else crear.mutate({ body: { ...datos, empresaId } }, fin);
  };

  return (
    <Modal
      titulo={cuenta ? "Corregir la cuenta" : "Nueva cuenta bancaria"}
      alCerrar={alCerrar}
      acciones={
        <Button size="sm" disabled={!listo || guardando} onClick={alGuardar}>
          {guardando ? "Guardando…" : cuenta ? "Guardar" : "Dar de alta"}
        </Button>
      }
    >
      <div className="mt-3 space-y-3">
        <Campo etiqueta="Banco" valor={banco} alCambiar={setBanco} />
        <Campo
          etiqueta="Número de cuenta"
          valor={numero}
          alCambiar={setNumero}
        />
        <label className="block">
          <span className="font-medium text-[12px]">Moneda</span>
          <select
            className="mt-0.5 w-full rounded border bg-background px-2 py-1.5 text-[12.5px]"
            value={moneda}
            onChange={(e) =>
              setMoneda(e.target.value === "USD" ? "USD" : "PEN")
            }
          >
            <option value="PEN">Soles</option>
            <option value="USD">Dólares</option>
          </select>
        </label>
        <Campo etiqueta="CCI (opcional)" valor={cci} alCambiar={setCci} />
        <Campo etiqueta="Alias (opcional)" valor={alias} alCambiar={setAlias} />
      </div>
    </Modal>
  );
}

/**
 * Retirar del maestro. El servidor decide si borra o da de baja.
 *
 * No se pregunta «¿seguro?» a secas: se dice lo que va a pasar, que es distinto
 * según lo que cuelgue, y eso solo lo sabe el servidor. Por eso el aviso llega
 * en la respuesta y no antes.
 */
export function useRetirar() {
  const qc = useQueryClient();
  const empresa = api.retirarEmpresa.useMutation();
  const cuenta = api.retirarCuenta.useMutation();

  const fin = {
    onSuccess: (r: {
      status: number;
      body: { dice?: string; borrada?: boolean; message?: string };
    }) => {
      if (r.status !== 200) {
        toast.error(r.body.message ?? "No se pudo retirar");
        return;
      }
      // Se distingue en el aviso: «borrada» y «dada de baja» no son lo mismo y
      // quien lo hizo tiene que saber cuál de las dos pasó.
      if (r.body.borrada) toast.success(r.body.dice ?? "Borrado");
      else toast.warning(r.body.dice ?? "Dado de baja");
      void qc.invalidateQueries();
    },
    onError: () => toast.error("No se pudo retirar"),
  };

  return {
    retirarEmpresa: (id: string) => empresa.mutate({ params: { id } }, fin),
    retirarCuenta: (id: string) => cuenta.mutate({ params: { id } }, fin),
    trabajando: empresa.isPending || cuenta.isPending,
  };
}

/** Devolver al maestro algo que se había dado de baja. */
export function useReactivar() {
  const qc = useQueryClient();
  const empresa = api.editarEmpresa.useMutation();
  const cuenta = api.editarCuenta.useMutation();
  const fin = {
    onSuccess: () => {
      toast.success("Vuelve a estar activa");
      void qc.invalidateQueries();
    },
    onError: () => toast.error("No se pudo reactivar"),
  };
  return {
    reactivarEmpresa: (id: string) =>
      empresa.mutate({ params: { id }, body: { activa: true } }, fin),
    reactivarCuenta: (id: string) =>
      cuenta.mutate({ params: { id }, body: { activa: true } }, fin),
  };
}
